import { metersToFeetInches } from './floorPlanUtils';

/**
 * Label placement WITHIN a dimension string.
 *
 * A tiered string is one line: a label that will not fit between its own
 * extension lines must not push the line onto a new row — rows are what the
 * tiers exist to cap. So the LABEL moves, in the two ways a drafter moves one:
 *
 *   1. to the other side of the dimension line, and
 *   2. along the line, by just enough to clear its neighbour.
 *
 * Shifted labels carry a leader back to the middle of what they measure.
 *
 * The first version stepped outward in whole label widths until it found free
 * space. At the room package's print scale — 1/8" = 1', where a 3 ft segment is
 * ~36 px and its label ~35 px — nearly every label collided, so they cascaded
 * a hundred px clear of the drawing on long diagonal leaders. Hence: flow, and
 * two sides.
 *
 * Pure. Each measurement gains `labelAlongPx` (signed shift from the segment's
 * midpoint), `labelFlipSide` (draw on the far side of the line) and
 * `labelLeader`.
 *
 * Design: claude-docs/2026-09-17-elevation-tiered-dimensions.md §3.4.
 */

/** Clear space between two labels sharing one side of a line, in px. */
const LABEL_GAP_PX = 4;
/** A shift smaller than this reads as centred and needs no leader. */
const LEADER_EPSILON_PX = 0.5;

/**
 * Pack labels along one side of one line: each keeps its own position unless
 * the previous label is in the way, in which case it moves just past it.
 * Returns the chosen centre per item, in input order.
 */
const flowPack = (items) => {
  const placed = new Map();
  let cursor = -Infinity;
  [...items]
    .sort((a, b) => a.mid - b.mid)
    .forEach((item) => {
      const half = item.labelWidth / 2;
      const centre = Math.max(item.mid, cursor + LABEL_GAP_PX + half);
      placed.set(item.index, centre);
      cursor = centre + half;
    });
  return placed;
};

/**
 * @param {Array} measurements  tiered measurements (carry `stringKey`).
 * @param {{fontPx?: number, measureWidth?: (label: string, fontPx: number) => number}} opts
 *   `fontPx` must be the size the label is DRAWN at, not the requested size —
 *   the renderer shrinks it to `fontSize / max(zoom, 2)`, and measuring the
 *   unshrunk size reserves about twice the room a label needs.
 * @returns {Array}
 */
export const placeStringLabels = (measurements, opts = {}) => {
  if (!Array.isArray(measurements) || measurements.length === 0) {
    return measurements ?? [];
  }
  const { fontPx = 7, measureWidth } = opts;
  const widthOf =
    typeof measureWidth === 'function'
      ? (label) => measureWidth(label, fontPx)
      : (label) => String(label ?? '').length * fontPx * 0.5;

  const along = (m, key) =>
    m.type === 'horizontal' ? (m[key]?.x ?? 0) : (m[key]?.y ?? 0);

  // Only tiered strings are placed this way; anything else keeps its label
  // centred, which is what the stagger layout assumes.
  const byString = new Map();
  measurements.forEach((m, index) => {
    if (!m.stringKey) return;
    if (!byString.has(m.stringKey)) byString.set(m.stringKey, []);
    byString.get(m.stringKey).push(index);
  });

  const placement = new Map();
  byString.forEach((indices) => {
    const items = indices
      .map((index) => {
        const m = measurements[index];
        const start = along(m, 'start');
        const end = along(m, 'end');
        return {
          index,
          mid: (start + end) / 2,
          span: Math.abs(end - start),
          labelWidth: widthOf(metersToFeetInches(m.distance)) + LABEL_GAP_PX,
        };
      })
      .sort((a, b) => a.mid - b.mid);

    // A label that fits between its own extension lines stays where it is, on
    // the near side. The tight ones alternate sides, so each side sees every
    // other label and the gaps double.
    const sides = [[], []];
    let nextTightSide = 0;
    items.forEach((item) => {
      if (item.labelWidth <= item.span) {
        sides[0].push(item);
        return;
      }
      sides[nextTightSide].push(item);
      nextTightSide = nextTightSide === 0 ? 1 : 0;
    });

    sides.forEach((side, sideIndex) => {
      const packed = flowPack(side);
      side.forEach((item) => {
        // Never let a label travel further than its own width: past that it
        // reads as belonging to something else, and the leader crosses the
        // drawing. A rare overlap is the better failure.
        const raw = packed.get(item.index) - item.mid;
        const shift = Math.max(
          -item.labelWidth,
          Math.min(item.labelWidth, raw)
        );
        placement.set(item.index, { shift, sideIndex });
      });
    });
  });

  return measurements.map((m, index) => {
    const chosen = placement.get(index);
    if (!chosen) return m;
    const { shift, sideIndex } = chosen;
    const moved = Math.abs(shift) > LEADER_EPSILON_PX;
    if (!moved && sideIndex === 0) return m;
    return {
      ...m,
      ...(moved ? { labelAlongPx: shift, labelLeader: true } : {}),
      ...(sideIndex === 1 ? { labelFlipSide: true } : {}),
    };
  });
};

/**
 * Drop the dimension STRINGS whose labels cannot fit at the scale the drawing
 * is being placed at.
 *
 * A string is one line, and a label can only move along it or to its other
 * side, so a string can carry about twice its own length in label. Below that
 * the labels overlap each other whatever the placement does — which is exactly
 * what a room-package elevation printed at 1/8" = 1' looked like: nine
 * ten-character labels fighting over 2.3 inches of paper.
 *
 * So the drawing gives up the DETAIL line first and keeps the major breaks and
 * the overall, which is what a small-scale elevation carries anyway; the detail
 * is read on a larger-scale sheet. `overall` is never dropped.
 *
 * Pure. Returns the measurements that survive.
 */
export const fitStringsToScale = (measurements, opts = {}) => {
  if (!Array.isArray(measurements) || measurements.length === 0) {
    return measurements ?? [];
  }
  const { fontPx = 7, measureWidth } = opts;
  const widthOf =
    typeof measureWidth === 'function'
      ? (label) => measureWidth(label, fontPx)
      : (label) => String(label ?? '').length * fontPx * 0.5;

  const along = (m, key) =>
    m.type === 'horizontal' ? (m[key]?.x ?? 0) : (m[key]?.y ?? 0);

  const strings = new Map();
  measurements.forEach((m) => {
    if (!m.stringKey) return;
    const entry = strings.get(m.stringKey) || {
      tier: m.tier,
      demand: 0,
      lo: Infinity,
      hi: -Infinity,
    };
    entry.demand += widthOf(metersToFeetInches(m.distance)) + LABEL_GAP_PX;
    entry.lo = Math.min(entry.lo, along(m, 'start'), along(m, 'end'));
    entry.hi = Math.max(entry.hi, along(m, 'start'), along(m, 'end'));
    strings.set(m.stringKey, entry);
  });

  const dropped = new Set();
  strings.forEach((entry, key) => {
    if (entry.tier === 'overall') return;
    const capacity = (entry.hi - entry.lo) * 2; // both sides of the line
    if (entry.demand > capacity) dropped.add(key);
  });

  if (dropped.size === 0) return measurements;
  return measurements.filter((m) => !m.stringKey || !dropped.has(m.stringKey));
};
